import SEO from '../components/SEO';
import Schema, { createProductSchema } from '../components/Schema';
import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, CheckCircle2, Store, BarChart3, Users, Smartphone, Globe, Zap, Shield, Check, RefreshCw, Cloud, Target, TrendingUp, Package, Settings } from 'lucide-react';
import ProductCTA from '../components/ProductCTA';

const EPoSNow = () => {
  const stats = [
    { value: "80,000+", label: "Global Locations" },
    { value: "100+", label: "App Integrations" },
    { value: "15 Min", label: "Quick Setup" },
    { value: "Real-time", label: "SAP B1 Sync" }
  ];

  const integrationBenefits = [
    { 
      icon: RefreshCw, 
      title: "Optimised Data Flow",
      strapline: "Seamless synchronisation",
      desc: "Two-way sync between EPOS and SAP Business One ensures consistency and eliminates manual data entry. Orders, payments, and inventory flow automatically."
    },
    { 
      icon: Zap, 
      title: "Increased Efficiency",
      strapline: "Automated processes",
      desc: "Automate sales tracking, inventory management, and customer data updates. Save time, reduce errors, and focus on growing your business."
    },
    { 
      icon: BarChart3, 
      title: "Enhanced Business Insights",
      strapline: "Real-time unified data",
      desc: "Access real-time data on sales, stock, and customer performance from a single platform. Make informed decisions from any device, anywhere."
    },
    { 
      icon: Users, 
      title: "Improved Customer Experience",
      strapline: "Personalised interactions",
      desc: "Unified view of customer data across all sales channels. Deliver personalised service whether customers shop in-store, online, or via mobile."
    },
    { 
      icon: TrendingUp, 
      title: "Scalability",
      strapline: "Grow without limits",
      desc: "Supports businesses from single locations to multi-store enterprises. Scale seamlessly as your business expands across regions."
    },
    { 
      icon: Cloud, 
      title: "Cloud Accessibility",
      strapline: "Access anywhere",
      desc: "Both EPoS Now and SAP Business One are cloud-based. Access your complete business data from anywhere with an internet connection."
    }
  ];

  const howItWorks = [
    { step: "API Gateway", desc: "SAP Business One API Gateway provides secure single endpoint for EPoS Now to access business data" },
    { step: "Automatic Sync", desc: "Payments, products, stock levels, and customer data synchronise automatically between systems" },
    { step: "Transaction-Level Tracking", desc: "Track every sale down to individual transaction level with end-of-day summaries" },
    { step: "Real-Time Updates", desc: "Inventory adjustments, new products, and customer changes reflect instantly in both systems" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="EPoS Now | Point of Sale Integration for SAP Business One"
        description="EPOS for retail and hospitality integrated with SAP B1. Real-time inventory sync, trade pricing, and seamless counter sales management."
        keywords="EPoS Now, SAP B1 POS, point of sale, retail POS, hospitality EPOS, trade counter"
        canonical="https://www.ascarii.com/products/epos-now"
      />
      
      {/* Hero */}
      <section className="relative min-h-[600px] bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-500 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-sky-500/20 backdrop-blur-sm border border-sky-400/30 rounded-full text-sm font-semibold mb-6">EPOS for SAP B1</div>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-sky-500 flex items-center justify-center">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">EPoS Now</h1>
              </div>
              
              <p className="text-xl md:text-2xl text-sky-100 mb-4 font-medium">Complete EPOS integrated with SAP Business One</p>
              <p className="text-lg text-sky-50 mb-8 leading-relaxed">Unified platform for retail and hospitality. Seamless two-way integration between your till and SAP Business One - automatic synchronisation of sales, inventory, and customer data.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white p-8">
                <img src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=800&q=85" alt="EPOS System" className="w-full h-[350px] object-cover rounded-lg" loading="eager" />
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0"><svg viewBox="0 0 1440 120" fill="none" className="w-full h-16"><path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/></svg></div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-sky-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAP B1 Integration Benefits - Showcase Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-500">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Integrate EPoS Now with SAP Business One?</h2>
            <p className="text-xl text-sky-100 max-w-4xl mx-auto">Create a unified platform for managing sales, inventory, and customer data</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrationBenefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-sky-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-sky-300 font-medium mb-2">{benefit.strapline}</p>
                    <p className="text-sky-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How EPoS Now Integrates with SAP Business One</h2>
            <p className="text-xl text-gray-600">Two-way synchronisation for seamless operations</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {howItWorks.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white rounded-xl p-6 border-l-4 border-sky-600 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.step}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect For - Industry Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perfect for Retail & Hospitality</h2>
            <p className="text-xl text-gray-600">SAP Business One users with customer-facing operations</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { 
                icon: Store, 
                title: "Retail Operations", 
                strapline: "Multi-channel commerce",
                points: ["Clothing & fashion stores", "Convenience & grocery shops", "Gift shops & speciality retail", "Multi-location retail chains", "Inventory across all channels", "Customer loyalty programmes"] 
              },
              { 
                icon: Users, 
                title: "Hospitality Businesses", 
                strapline: "Front & back of house",
                points: ["Pubs and bars", "Restaurants & fine dining", "Cafes and coffee shops", "Quick service restaurants", "Table management & reservations", "Kitchen display integration"] 
              }
            ].map((sector, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-slate-200 hover:border-sky-300 hover:shadow-xl transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-sky-500/10 to-cyan-500/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <sector.icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{sector.title}</h3>
                    <p className="text-sm text-sky-600 font-medium mb-4">{sector.strapline}</p>
                    <ul className="space-y-2">
                      {sector.points.map((point, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-green-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Connect Your Till to SAP Business One</h2>
              <p className="text-xl text-gray-600 mb-8">Seamless EPOS integration for retail and hospitality</p>
              <div className="space-y-6">
                {[
                  { icon: Shield, title: "Proven Integration", desc: "Trusted by businesses worldwide" },
                  { icon: Zap, title: "Quick Setup", desc: "Operational in days, not weeks" },
                  { icon: Target, title: "Complete Solution", desc: "Till to back office unified" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-cyan-500/10 flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-sky-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA 
                productName="EPoS Now for SAP Business One" 
                brandColor="green-600"
                headline="Ready to Unify Your EPOS and ERP?"
                description="Let's discuss how EPoS Now integrates with SAP Business One to streamline your retail or hospitality operations."
                benefits={[
                  "Two-way SAP B1 synchronisation",
                  "Real-time inventory updates",
                  "Unified customer data",
                  "Cloud-based accessibility"
                ]}
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EPoSNow;
